// ============================================================
// applyHits/crits - critical effects, then the death they may bring.
//
// ROLL ORDER CONTRACT: each hit's critical dice, in hit order, before
// any weapon special rolls anything.
// ============================================================
import { rollCritDice, mergeCondition, condType } from '../../engine/index';
import { isConditionImmune, isDeadSkip, applyDeath } from '../../engine/postHitEffects';
import { activeProtagonist } from '../../engine/survival/protagonist';
import { resolveMortalBlow } from '../../engine/survival/deathClock';
import type { HitResult } from '../../engine/damageCalc';
import { narrEvent, replaceAll, type HitLedger } from './ledger';

/** Crippled / Destroyed carry the hit location - only for body types with distinct locations. */
function locatedCode(ledger: HitLedger, code: string, locName: string): string {
  if (!ledger.bodyConfig.supportsConditionLocations) return code;
  return code === 'Cr' || code === 'De' ? code + ':' + locName : code;
}

/**
 * Is the target immune to this code? Says why in the log when the engine
 * gives a reason. Weapon specials matter: Warp Weapons bypass the Astartes
 * Blood Loss immunity (v6.28.0).
 */
function immuneTo(ledger: HitLedger, code: string): boolean {
  const immunity = isConditionImmune(code, ledger.tgt, ledger.sp);
  if (immunity.immune && immunity.reason) {
    narrEvent(ledger, { name: 'Immunity', icon: '\uD83D\uDEE1', result: immunity.reason });
  }
  return immunity.immune;
}

/** One code of a critical's effects: immunity, death, or a condition merged onto the target. */
function applyCritCode(ledger: HitLedger, code: string, locName: string): void {
  if (!code || immuneTo(ledger, code)) return;
  if (code === 'K') {
    ledger.targetKilled = true;
    return;
  }
  // Skip conditions on dead targets.
  if ((ledger.alreadyDead || ledger.targetKilled) && isDeadSkip(code)) return;
  mergeCondition(ledger.newConds, locatedCode(ledger, code, locName), ledger.stacking);
}

/** Roll one hit's critical dice (rewriting its text and effects in place), then apply each effect. */
function applyCrit(ledger: HitLedger, h: HitResult & { crit: NonNullable<HitResult['crit']> }): void {
  const rolled = rollCritDice(h.crit.text, h.crit.fx);
  h.crit.text = rolled.text;
  h.crit.fx = rolled.fx;
  if (rolled.rolls.length > 0) {
    const critDiceText = '🎲 Critical dice: ' + rolled.rolls.map((r) => r.expr + ' → ' + r.value).join(', ');
    narrEvent(ledger, { name: 'Critical', icon: '🎲', result: critDiceText });
  }
  const locName = h.location?.name || 'Unknown';
  rolled.fx.split(',').forEach((c) => applyCritCode(ledger, c.trim(), locName));
}

/** Every landed hit's critical, in hit order. */
export function applyCriticals(ledger: HitLedger): void {
  for (const h of ledger.data.hitsAfterDefence) {
    if (h.crit && h.crit.fx) applyCrit(ledger, h as HitResult & { crit: NonNullable<HitResult['crit']> });
  }
}

/**
 * The death floor. Checked AFTER the whole fx loop rather than inside it,
 * so the floor sees the final verdict for the hit rather than one code.
 *
 * ANOTHER MORTAL WOUND CLOSES THE WINDOW. `mergeCondition` under the Round
 * Countdown ruling adds durations, so a second Dy3 on a man at Dy3 left him
 * at Dy6 - one combatant took seven killing blows and reached Dy21.
 */
function applyDeathFloor(ledger: HitLedger): void {
  if (!ledger.targetKilled) return;
  const deathFloor = resolveMortalBlow(activeProtagonist(ledger.tgt, ledger.protagonistRules), ledger.newConds);
  if (!deathFloor) return;
  if (deathFloor.fatal) {
    // The clock ran out under the blow: only `dyingFatal` reaches here.
    narrEvent(ledger, { name: 'Death', icon: '💀', result: deathFloor.note });
    return;
  }
  // He is not dead, and the log still says what the table said: the
  // critical's own text has been narrated in full, then this.
  ledger.targetKilled = false;
  // Written, not merged: `resolveMortalBlow` has already decided what the
  // clock should read, and merging would add to it again.
  for (let i = ledger.newConds.length - 1; i >= 0; i--) {
    if (condType(ledger.newConds[i]) === 'Dy') ledger.newConds.splice(i, 1);
  }
  for (const c of deathFloor.conditions) mergeCondition(ledger.newConds, c, 'refresh');
  narrEvent(ledger, { name: 'Survived', icon: '✴', result: deathFloor.note });
}

/** The death floor, then death itself if the kill stood. */
export function resolveDeath(ledger: HitLedger): void {
  applyDeathFloor(ledger);
  if (!ledger.targetKilled) return;
  replaceAll(ledger.newConds, applyDeath(ledger.newConds));
  narrEvent(ledger, { name: 'Death', icon: '💀', result: '💀 TARGET IS DEAD!' });
}
